// Campi modello WSLam v2 (chkWslam v1) — port da assets/js/map-wslam.js
// (v1 resta la fonte di verità per gli algoritmi; non divergere).
//
// ARCHITETTURA v2 (deliberata, DIVERSA dalla v1): il campo NON si ridisegna a
// ogni moveend. Viene renderizzato UNA volta sul canvas-dominio fisso (bbox
// [11.8,35.3]-[16.2,39.0], 1100×900, condiviso con interpol.js) e montato come
// source MapLibre type 'canvas' (animate:false): la GPU riproietta durante
// pan/zoom. Si ricalcola SOLO al cambio prodotto/scadenza.
//
// Catalogo centrale Model Fields (prodotti griglia CMEMS/
// LAM: sst, lam_temp, …) + /api/forecast-map.php?mode=timeline (prodotti terra
// points-field: temp, precipitation_mm, wind_avg, wind_gust, humidity,
// pressure = TERRAIN_PRODUCTS) + composito temp_combined (sst+lam_temp,
// tolleranza accoppiamento step 15', addCompositeTemperatureProduct 1:1).
//
// Algoritmi portati 1:1 (riferimenti map-wslam.js):
// - campionamento griglia sampleGridValue (bilinear 2×2 + fallback IDW
//   1/max(0.18,d²) su intorno 6×6 con cutoff 2.7 celle);
// - palette colorFromPalette (step / keys interpolate / min-max lineare),
//   palette WSPalettes/prodotto IDENTICHE (field.palette ?? product.palette);
// - campo griglia: cell = max(2, min(5, round(width/320))) px, alpha
//   round(205·softAlpha·max(0.38, min(1, coverage))), upscale smooth, righe/
//   colonne separabili (Mercator) e render a blocchi ~8ms (path GL v1);
// - campo points-field (_drawPointField): cell = max(5, min(10,
//   round(width/150))), raggio = max(76, min(145, width/8)), peso
//   1/pow(max(18,d²),1.12), alpha 0.18+fade·0.38 con fade = 1−√d/raggio;
// - maschera dominio _applyDomainMask: fullDomain → nessun ritaglio,
//   domain 'land' → keep-terra, altrimenti punch-terra (maschera Sicilia
//   rasterizzata condivisa, compositeSicilyMask di interpol.js);
// - softSeaMaskAlpha/softGridEdgeAlpha portate (default OFF come chkWslam v1);
// - scadenze: available_steps / items_by_step / nearestStepForTime /
//   stepTimeMs / nearestStepIndexForTime portate 1:1;
// - click: valueAtLatLng port (points: più vicino entro 0.16 deg²; grid:
//   sampleGridValue; gating terra/mare via point-in-polygon Sicilia).
//
// Adattamenti dichiarati:
// - il canvas-dominio sostituisce il viewport: cell/raggio derivano da
//   width=1100 fissa (cell griglia = 3px-dominio, cell points = 7,
//   raggio points = 137.5) invece che dallo schermo;
// - i punti points-field sono filtrati sul bbox-dominio pad(0.35) invece che
//   sui bounds mappa pad(0.35) (stesso pad, finestra fissa);
// - il composito temp_combined è renderizzato su un unico canvas: ogni
//   componente su canvas d'appoggio → maschera del proprio dominio → blit in
//   ordine sea→land (equivalente ai due layer impilati v1);
// - opacità: raster-opacity del layer (v1: opacity CSS del canvas), default
//   0.82, clamp 0.2–1 come lo slider v1.
// GAP dichiarati: animazione motion (particelle vento / onde griglia) NON
// portata in questa fase (canvas statico animate:false — fase successiva);
// UI timeline/bottoni prodotto v1 non qui (li monta il pannello v2);
// wslamFieldForTime/fieldForNearestAvailableStep (API per earth) non portate.
import {
    DOMAIN, DOMAIN_COORDS, domainPoint, domainLatLng,
    compositeSicilyMask, loadSicilyFeature, getSicilyFeature, featureContainsLatLng,
    refreshCanvasSource,
} from './interpol.js';
import { getWslamTemporalRange, selectWslamStepAtTime } from '../lib/wslam-time.js';

const API_URL = '/api/weather.php?path=%2Fmodel-fields%2Fcatalog';
const FORECAST_API_URL = '/api/forecast-map.php?mode=timeline';
const COMPOSITE_STEP_TOLERANCE_MS = 15 * 60 * 1000;
const DEFAULT_BOUNDS = [[35.35, 10.65], [39.05, 17.45]]; // [[S,W],[N,E]] come v1

const SRC = 'ws-wslam-src';
const LYR = 'ws-wslam';
const STATIONS_LYR = 'ws-stations-symbols';

// Port 1:1 di TERRAIN_PRODUCTS (map-wslam.js:10-85).
const TERRAIN_PRODUCTS = {
    terrain_temp: {
        label: 'Temperatura suolo', short_label: 'T suolo', unit: 'C', domain: 'land',
        source: 'Dati previsionali puntuali', mode: 'points-field', valueKey: 'temp',
        description: 'Campo interpolato da valori numerici previsionali sul territorio',
        scale: [6, 34],
        palette: ['#2a4bff', '#19c7d4', '#33d456', '#f3d45b', '#ff8b2f', '#d7191c'],
    },
    terrain_rain: {
        label: 'Pioggia suolo', short_label: 'Pioggia', unit: 'mm', domain: 'land',
        source: 'Dati previsionali puntuali', mode: 'points-field', valueKey: 'precipitation_mm',
        description: 'Precipitazione interpolata da valori numerici previsionali',
        scale: [0, 18],
        palette: ['#dff7ff', '#63d3ff', '#2187ff', '#6f43d6', '#f04fa3', '#b00020'],
    },
    terrain_wind: {
        label: 'Vento suolo', short_label: 'Vento', unit: 'km/h', domain: 'land',
        source: 'Dati previsionali puntuali', mode: 'points-field', valueKey: 'wind_avg',
        vectorKey: 'wind_direction',
        description: 'Vento medio interpolato da valori numerici previsionali',
        scale: [0, 55],
        palette: ['#e8f7ee', '#a7df8e', '#49b675', '#168aad', '#4257b2', '#6c2d91'],
    },
    terrain_gust: {
        label: 'Raffiche suolo', short_label: 'Raff.', unit: 'km/h', domain: 'land',
        source: 'Dati previsionali puntuali', mode: 'points-field', valueKey: 'wind_gust',
        vectorKey: 'wind_direction',
        description: 'Raffiche interpolate da valori numerici previsionali',
        scale: [0, 80],
        palette: ['#fff7bc', '#fee391', '#fec44f', '#fe9929', '#d95f0e', '#7f2704'],
    },
    terrain_humidity: {
        label: 'Umidita suolo', short_label: 'UR', unit: '%', domain: 'land',
        source: 'Dati previsionali puntuali', mode: 'points-field', valueKey: 'humidity',
        description: 'Umidita relativa interpolata da valori numerici previsionali',
        scale: [25, 100],
        palette: ['#f7fcf0', '#ccebc5', '#7bccc4', '#2b8cbe', '#08589e'],
    },
    terrain_pressure: {
        label: 'Pressione suolo', short_label: 'Press.', unit: 'hPa', domain: 'land',
        source: 'Dati previsionali puntuali', mode: 'points-field', valueKey: 'pressure',
        description: 'Pressione interpolata da valori numerici previsionali',
        scale: [1000, 1030],
        palette: ['#5e4fa2', '#3288bd', '#66c2a5', '#fee08b', '#f46d43', '#9e0142'],
    },
};

const state = {
    map: null,
    active: false,
    loaded: false,
    catalog: null,
    catalogPromise: null,
    forecastPayload: null,
    product: 'temp_combined',
    index: 0,
    opacity: 0.82,
    softSeaMask: false, // parità default chkWslam v1 (activateWslamLayer())
    fieldCache: new Map(),
    currentField: null,
    overlaySeq: 0,
    timeRequestSeq: 0,
    renderGen: 0,
    canvas: null,
    popup: null,
};

// ======== Helper numerici/tempo (port map-wslam.js) ==========================
function numberOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : null;
}

function firstValue(...values) {
    return values.find(value => value !== null && value !== undefined && value !== '');
}

function stepTime(step) {
    const parsed = Date.parse(String(firstValue(step?.valid_time, step?.datetime, step?.time, step?.key) || ''));
    return Number.isFinite(parsed) ? parsed : 0;
}

// (alias v1: stepTimeMs — identica a stepTime)
function stepTimeMs(step) {
    return stepTime(step);
}

export function nearestStepForTime(steps, targetMs) {
    if (!Array.isArray(steps) || !steps.length || !Number.isFinite(targetMs)) return null;
    return steps.reduce((best, step) => {
        const time = stepTime(step);
        if (!time) return best;
        if (!best) return step;
        return Math.abs(time - targetMs) < Math.abs(stepTime(best) - targetMs) ? step : best;
    }, null);
}

function nearestStepIndexForTime(targetMs, productId = state.product) {
    const steps = stepsForProduct(productId);
    if (!steps.length || !Number.isFinite(targetMs)) return -1;
    let bestIndex = -1;
    let bestDistance = Infinity;
    steps.forEach((step, index) => {
        const time = stepTimeMs(step);
        if (!time) return;
        const distance = Math.abs(time - targetMs);
        if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
        }
    });
    return bestIndex;
}

function compassToRadians(value) {
    if (value === null || value === undefined) return null;
    const numeric = Number.parseFloat(value);
    if (Number.isFinite(numeric)) return (numeric - 90) * Math.PI / 180;
    const key = String(value).trim().toUpperCase();
    const sectors = {
        N: 270, NNE: 292.5, NE: 315, ENE: 337.5,
        E: 0, ESE: 22.5, SE: 45, SSE: 67.5,
        S: 90, SSW: 112.5, SW: 135, WSW: 157.5,
        W: 180, WNW: 202.5, NW: 225, NNW: 247.5,
    };
    return Object.prototype.hasOwnProperty.call(sectors, key) ? sectors[key] * Math.PI / 180 : null;
}

// ======== Catalogo (port loadCatalog + merge + composito) ====================
function stepsForProductFromCatalog(_catalog, product) {
    return Array.isArray(product?.available_steps) ? product.available_steps : [];
}

function stepsForProduct(productId = state.product) {
    const product = state.catalog?.products?.[productId];
    return Array.isArray(product?.available_steps) ? product.available_steps : [];
}

function currentStep() {
    return stepsForProduct()[state.index] || null;
}

function currentProduct() {
    return state.catalog?.products?.[state.product] || null;
}

function terrainPointsForStep(step, product = currentProduct()) {
    if (!state.forecastPayload || product?.mode !== 'points-field') return [];
    const rows = state.forecastPayload.items_by_step?.[step?.forecast_key || step?.key] || [];
    return rows.map(item => {
        const lat = numberOrNull(firstValue(item.lat, item.latitude));
        const lon = numberOrNull(firstValue(item.lon, item.lng, item.longitude));
        const value = numberOrNull(item?.[product.valueKey]);
        if (lat === null || lon === null || value === null) return null;
        return {
            lat,
            lon,
            value,
            name: item.name || item.comune || '',
            vector: compassToRadians(item[product.vectorKey]),
        };
    }).filter(Boolean);
}

function mergeTerrainCatalog(catalog, forecastPayload) {
    if (!forecastPayload?.success || !Array.isArray(forecastPayload.available_steps) || !forecastPayload.items_by_step) return catalog;
    const hasLamLandProducts = Object.values(catalog?.products || {}).some(product => product?.kind === 'lam' && product?.domain === 'land');
    if (hasLamLandProducts) return catalog;

    const terrainProducts = {};
    Object.entries(TERRAIN_PRODUCTS).forEach(([id, product]) => {
        const availableSteps = forecastPayload.available_steps.map(entry => ({
            key: entry.key,
            forecast_key: entry.key,
            label: entry.label || entry.short_label || entry.key,
            valid_time: entry.key,
            field: true,
        })).filter(step => (forecastPayload.items_by_step?.[step.key] || []).some(item => numberOrNull(item?.[product.valueKey]) !== null));
        if (!availableSteps.length) return;
        terrainProducts[id] = { ...product, available_steps: availableSteps };
    });

    return {
        ...catalog,
        products: {
            ...(catalog.products || {}),
            ...terrainProducts,
        },
    };
}

function addCompositeTemperatureProduct(catalog) {
    const products = catalog?.products || {};
    const seaProduct = products.sst;
    const landProduct = products.lam_temp;
    const seaSteps = stepsForProductFromCatalog(catalog, seaProduct);
    const landSteps = stepsForProductFromCatalog(catalog, landProduct);
    if (!seaProduct || !landProduct || !seaSteps.length || !landSteps.length) return catalog;

    const availableSteps = seaSteps.map(seaStep => {
        const seaMs = stepTime(seaStep);
        const landStep = nearestStepForTime(landSteps, seaMs);
        if (!landStep) return null;
        const landMs = stepTime(landStep);
        if (!seaMs || !landMs || Math.abs(seaMs - landMs) > COMPOSITE_STEP_TOLERANCE_MS) return null;
        return {
            key: `temp_${seaStep.key || seaStep.valid_time}_${landStep.key || landStep.valid_time}`,
            label: seaStep.label || landStep.label || '',
            valid_time: seaStep.valid_time || landStep.valid_time || '',
            field: true,
            components: {
                sea: { product: 'sst', step: seaStep },
                land: { product: 'lam_temp', step: landStep },
            },
        };
    }).filter(Boolean);

    if (!availableSteps.length) return catalog;

    const displayProducts = {
        ...products,
        sst: { ...seaProduct, short_label: seaProduct.short_label || 'SST' },
        lam_temp: { ...landProduct, label: 'Temperatura territorio LAM', short_label: 'LAM' },
    };

    return {
        ...catalog,
        products: {
            temp_combined: {
                kind: 'composite',
                label: 'Temperatura mare + territorio',
                short_label: 'Temp',
                unit: 'C',
                domain: 'mixed',
                source: 'CMEMS mare + WSLAM LAM 2KM GRIB',
                mode: 'composite-field',
                description: 'Temperatura mare CMEMS e temperatura territorio LAM/GRIB su un unico campo bilinear',
                available_steps: availableSteps,
            },
            ...displayProducts,
        },
    };
}

function catalogHasLamLandProducts(catalog) {
    return Object.values(catalog?.products || {}).some(product => product?.kind === 'lam' && product?.domain === 'land');
}

async function loadCatalog() {
    if (state.loaded && state.catalog) return state.catalog;
    if (!state.catalogPromise) {
        state.catalogPromise = (async () => {
            const catalogResponse = await fetch(API_URL, { headers: { Accept: 'application/json' } });
            if (!catalogResponse.ok) throw new Error(`WSLAM catalog ${catalogResponse.status}`);
            const catalogPayload = await catalogResponse.json();
            let catalog = catalogPayload?.data;
            if (!catalogPayload?.success || !catalog) throw new Error('Catalogo Model Fields non valido');

            if (!catalogHasLamLandProducts(catalog)) {
                const forecastResponse = await fetch(FORECAST_API_URL, { headers: { Accept: 'application/json' } }).catch(() => null);
                if (forecastResponse?.ok) {
                    state.forecastPayload = await forecastResponse.json();
                    catalog = mergeTerrainCatalog(catalog, state.forecastPayload);
                }
            }
            catalog = addCompositeTemperatureProduct(catalog);
            const ok = catalog?.ok && Object.values(catalog.products || {}).some(product => stepsForProductFromCatalog(catalog, product).length);
            if (!ok) throw new Error('Catalogo WSLAM vuoto');
            state.catalog = catalog;
            state.loaded = true;
            const initialIndex = nearestStepIndexForTime(Date.now(), state.product);
            if (initialIndex >= 0) state.index = initialIndex;
            return catalog;
        })();
    }
    try {
        return await state.catalogPromise;
    } finally {
        state.catalogPromise = null;
    }
}

function ensureAvailableProduct() {
    const products = state.catalog?.products || {};
    if (products[state.product] && stepsForProduct(state.product).length) {
        state.index = Math.min(state.index, Math.max(0, stepsForProduct(state.product).length - 1));
        return;
    }
    const first = Object.keys(products).find(id => stepsForProduct(id).length);
    if (first) {
        state.product = first;
        state.index = 0;
    }
}

// ======== Palette (port hexToRgb/colorFromPalette) ===========================
function hexToRgb(hex) {
    const clean = String(hex || '').replace('#', '');
    return clean.length === 6
        ? [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)]
        : [255, 255, 255];
}

function colorFromPalette(value, scale, palette, paletteKeys, paletteMode) {
    if (paletteMode === 'step' && Array.isArray(paletteKeys) && Array.isArray(palette) && paletteKeys.length > 1 && palette.length >= paletteKeys.length) {
        const keys = paletteKeys.map(Number);
        const colors = palette.map(hexToRgb);
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return colors[0];
        if (numericValue < keys[0]) return colors[0];
        for (let index = 0; index < keys.length - 1; index += 1) {
            if (numericValue < keys[index + 1]) return colors[index];
        }
        return colors[Math.min(colors.length - 1, keys.length)];
    }
    if (Array.isArray(paletteKeys) && Array.isArray(palette) && paletteKeys.length === palette.length && palette.length > 1) {
        const keys = paletteKeys.map(Number);
        const colors = palette.map(hexToRgb);
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return colors[0];
        if (numericValue <= keys[0]) return colors[0];
        if (numericValue >= keys[keys.length - 1]) return colors[colors.length - 1];
        for (let index = 0; index < keys.length - 1; index += 1) {
            if (numericValue > keys[index + 1]) continue;
            const span = Math.max(0.0001, keys[index + 1] - keys[index]);
            const local = Math.max(0, Math.min(1, (numericValue - keys[index]) / span));
            const a = colors[index];
            const b = colors[index + 1];
            return [
                Math.round(a[0] + (b[0] - a[0]) * local),
                Math.round(a[1] + (b[1] - a[1]) * local),
                Math.round(a[2] + (b[2] - a[2]) * local),
            ];
        }
        return colors[colors.length - 1];
    }
    const [min, max] = scale || [0, 1];
    const colors = (palette || ['#2b8cbe', '#ffffbf', '#d7191c']).map(hexToRgb);
    const t = Math.max(0, Math.min(1, (value - min) / Math.max(0.0001, max - min)));
    const position = t * (colors.length - 1);
    const index = Math.min(colors.length - 2, Math.max(0, Math.floor(position)));
    const local = position - index;
    const a = colors[index];
    const b = colors[index + 1];
    return [
        Math.round(a[0] + (b[0] - a[0]) * local),
        Math.round(a[1] + (b[1] - a[1]) * local),
        Math.round(a[2] + (b[2] - a[2]) * local),
    ];
}

// PERF scadenze (2026-07-03): colorFromPalette riparsava palette+keys (map
// Number/hexToRgb) a OGNI cella → ~110k allocazioni per componente e render di
// molti secondi ("la scadenza non fa nulla"). Sampler con memo per valore
// quantizzato a 0.05 (invisibile a palette che spandono su decine di unità):
// il colore si calcola una volta per valore, non una volta per pixel.
function makeColorSampler(scale, palette, paletteKeys, paletteMode) {
    const memo = new Map();
    return (value) => {
        const q = Math.round(value * 20); // quantizzazione 0.05
        let c = memo.get(q);
        if (c === undefined) {
            c = colorFromPalette(q / 20, scale, palette, paletteKeys, paletteMode);
            memo.set(q, c);
        }
        return c;
    };
}

// ======== Campionamento griglia (port sampleGridValue) =======================
function sampleGridValue(grid, lat, lng, key = 'values', fallback = true) {
    const values = grid?.[key];
    if (!grid?.bounds || !values?.length || !grid.width || !grid.height) return null;
    const south = grid.bounds[0][0];
    const west = grid.bounds[0][1];
    const north = grid.bounds[1][0];
    const east = grid.bounds[1][1];
    if (lat < south || lat > north || lng < west || lng > east) return null;

    const gx = ((lng - west) / Math.max(0.0001, east - west)) * (grid.width - 1);
    const gy = ((north - lat) / Math.max(0.0001, north - south)) * (grid.height - 1);
    const x0 = Math.max(0, Math.min(grid.width - 1, Math.floor(gx)));
    const y0 = Math.max(0, Math.min(grid.height - 1, Math.floor(gy)));
    const x1 = Math.max(0, Math.min(grid.width - 1, x0 + 1));
    const y1 = Math.max(0, Math.min(grid.height - 1, y0 + 1));
    const tx = Math.max(0, Math.min(1, gx - x0));
    const ty = Math.max(0, Math.min(1, gy - y0));
    const v00 = values[y0 * grid.width + x0];
    const v10 = values[y0 * grid.width + x1];
    const v01 = values[y1 * grid.width + x0];
    const v11 = values[y1 * grid.width + x1];
    if ([v00, v10, v01, v11].every(value => value !== null && value !== undefined && Number.isFinite(Number(value)))) {
        const top = Number(v00) * (1 - tx) + Number(v10) * tx;
        const bottom = Number(v01) * (1 - tx) + Number(v11) * tx;
        return { value: top * (1 - ty) + bottom * ty, coverage: 1 };
    }
    if (!fallback) return null;

    let weighted = 0;
    let weights = 0;
    for (let yy = Math.max(0, y0 - 2); yy <= Math.min(grid.height - 1, y0 + 3); yy += 1) {
        for (let xx = Math.max(0, x0 - 2); xx <= Math.min(grid.width - 1, x0 + 3); xx += 1) {
            const value = values[yy * grid.width + xx];
            if (value === null || value === undefined || !Number.isFinite(Number(value))) continue;
            const dx = gx - xx;
            const dy = gy - yy;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > 2.7) continue;
            const weight = 1 / Math.max(0.18, distance * distance);
            weighted += Number(value) * weight;
            weights += weight;
        }
    }
    return weights ? { value: weighted / weights, coverage: Math.min(1, weights / 5) } : null;
}

// ======== Fetch/field per step (port fetchFieldPayload/fieldForStep) =========
async function fetchFieldPayload(url, productId, stepKey) {
    if (!url) return null;
    const cacheKey = `${productId}:${stepKey || ''}:${url}`;
    if (state.fieldCache.has(cacheKey)) return state.fieldCache.get(cacheKey);
    const request = fetch(url, { headers: { Accept: 'application/json' } })
        .then(response => {
            if (!response.ok) throw new Error(`WSLAM field ${response.status}`);
            return response.json();
        })
        .then(responsePayload => {
            const payload = responsePayload?.data || responsePayload;
            state.fieldCache.set(cacheKey, payload);
            if (state.fieldCache.size > 48) {
                state.fieldCache.delete(state.fieldCache.keys().next().value);
            }
            return payload;
        })
        .catch(error => {
            state.fieldCache.delete(cacheKey);
            throw error;
        });
    state.fieldCache.set(cacheKey, request);
    return request;
}

async function fieldForStep(step, product, productId = state.product) {
    if (product?.mode === 'points-field') {
        return {
            ok: true,
            kind: 'points',
            product: productId,
            valid_time: step?.valid_time || step?.key || '',
            scale: product.scale,
            palette: product.palette,
            points: terrainPointsForStep(step, product),
        };
    }
    if (product?.mode === 'composite-field') {
        const components = step?.components || {};
        const entries = await Promise.all(Object.entries(components).map(async ([role, component]) => {
            const componentProduct = state.catalog?.products?.[component.product];
            const componentStep = component.step;
            const payload = await fetchFieldPayload(componentStep?.field_url, component.product, componentStep?.key);
            if (!payload?.ok || !componentProduct) return null;
            return {
                role,
                productId: component.product,
                product: componentProduct,
                step: componentStep,
                field: {
                    ...payload,
                    scale: payload.scale || componentProduct.scale,
                    palette: payload.palette || componentProduct.palette,
                    paletteKeys: payload.paletteKeys || componentProduct.paletteKeys,
                    paletteMode: payload.paletteMode || componentProduct.paletteMode,
                },
            };
        }));
        return {
            ok: entries.some(Boolean),
            kind: 'composite',
            product: productId,
            valid_time: step?.valid_time || step?.key || '',
            scale: product.scale,
            palette: product.palette,
            paletteKeys: product.paletteKeys,
            paletteMode: product.paletteMode,
            fields: entries.filter(Boolean),
        };
    }
    const url = step?.field_url;
    return fetchFieldPayload(url, productId, step?.key);
}

// ======== Maschere soft (port softSeaMaskAlpha/softGridEdgeAlpha) ============
function softSeaMaskAlpha(latlng) {
    if (!latlng) return 1;
    const dx = (latlng.lng - 14.05) / 3.55;
    const dy = (latlng.lat - 37.42) / 2.55;
    const normalized = Math.sqrt((dx * dx) + (dy * dy));
    if (normalized <= 0.78) return 1;
    if (normalized >= 1.08) return 0;
    const t = (normalized - 0.78) / 0.30;
    return 1 - (t * t * (3 - (2 * t)));
}

// bounds = { n, s, e, w } (equivalente L.LatLngBounds della v1).
function softGridEdgeAlpha(latlng, bounds) {
    if (!latlng || !bounds) return 1;
    const fade = 0.90;
    const north = Math.max(0, bounds.n - latlng.lat);
    const south = Math.max(0, latlng.lat - bounds.s);
    const east = Math.max(0, bounds.e - latlng.lng);
    const west = Math.max(0, latlng.lng - bounds.w);
    const edge = Math.min(north, south, east, west);
    if (edge >= fade) return 1;
    const t = Math.max(0, Math.min(1, edge / fade));
    return t * t * (3 - (2 * t));
}

// ======== Bounds helpers (equivalenti L.latLngBounds/pad/contains) ===========
function gridBoundsOf(field) {
    const raw = Array.isArray(field?.grid?.bounds) ? field.grid.bounds : DEFAULT_BOUNDS;
    return { s: raw[0][0], w: raw[0][1], n: raw[1][0], e: raw[1][1] };
}

function padBounds(bounds, ratio) {
    const dLat = (bounds.n - bounds.s) * ratio;
    const dLng = (bounds.e - bounds.w) * ratio;
    return { n: bounds.n + dLat, s: bounds.s - dLat, e: bounds.e + dLng, w: bounds.w - dLng };
}

function boundsContains(bounds, lat, lng) {
    return lat >= bounds.s && lat <= bounds.n && lng >= bounds.w && lng <= bounds.e;
}

// ======== Render sul canvas-dominio ==========================================
// Port di _drawGridFieldChunked (path GL v1): Mercator separabile
// (lat solo da y, lng solo da x), blocchi ~8ms, output identico.
function drawGridFieldChunked(targetCtx, field, product, gen) {
    return new Promise(resolve => {
        const grid = field.grid;
        if (!grid?.values?.length || !grid.width || !grid.height) { resolve(); return; }
        const scale = field.scale || product.scale;
        const palette = field.palette || product.palette;
        const paletteKeys = field.paletteKeys || product.paletteKeys;
        const paletteMode = field.paletteMode || product.paletteMode;
        const size = { x: DOMAIN.width, y: DOMAIN.height };
        const cell = Math.max(2, Math.min(5, Math.round(size.x / 320)));
        const cols = Math.max(1, Math.ceil(size.x / cell));
        const rows = Math.max(1, Math.ceil(size.y / cell));
        const fieldCanvas = document.createElement('canvas');
        fieldCanvas.width = cols;
        fieldCanvas.height = rows;
        const fieldCtx = fieldCanvas.getContext('2d', { alpha: true });
        const image = fieldCtx.createImageData(cols, rows);
        const bounds = padBounds(gridBoundsOf(field), 0.02);
        const soft = state.softSeaMask && product.domain !== 'land';
        const colorAt = makeColorSampler(scale, palette, paletteKeys, paletteMode);

        const lngByCol = new Array(cols);
        for (let col = 0; col < cols; col += 1) {
            lngByCol[col] = domainLatLng((col + 0.5) * cell, 0).lng;
        }
        const latByRow = new Array(rows);
        for (let row = 0; row < rows; row += 1) {
            latByRow[row] = domainLatLng(0, (row + 0.5) * cell).lat;
        }

        let row = 0;
        const step = () => {
            if (gen !== state.renderGen) { resolve(); return; }
            const t0 = performance.now();
            while (row < rows) {
                for (let col = 0; col < cols; col += 1) {
                    const lat = latByRow[row];
                    const lng = lngByCol[col];
                    if (!boundsContains(bounds, lat, lng)) continue;
                    const softAlpha = soft
                        ? softSeaMaskAlpha({ lat, lng }) * softGridEdgeAlpha({ lat, lng }, bounds)
                        : 1;
                    if (softAlpha <= 0) continue;
                    const sample = sampleGridValue(grid, lat, lng, 'values', true);
                    if (!sample) continue;
                    const [r, g, b] = colorAt(sample.value);
                    const offset = (row * cols + col) * 4;
                    image.data[offset] = r;
                    image.data[offset + 1] = g;
                    image.data[offset + 2] = b;
                    image.data[offset + 3] = Math.round(205 * softAlpha * Math.max(0.38, Math.min(1, sample.coverage || 1)));
                }
                row += 1;
                if (performance.now() - t0 > 14) break; // cede il main thread
            }
            if (row < rows) { requestAnimationFrame(step); return; }
            fieldCtx.putImageData(image, 0, 0);
            targetCtx.imageSmoothingEnabled = true;
            targetCtx.imageSmoothingQuality = 'high';
            targetCtx.drawImage(fieldCanvas, 0, 0, size.x, size.y);
            resolve();
        };
        step(); // primo blocco subito (budget 8ms), il resto nei frame dopo
    });
}

// Port di _drawPointField (IDW dai punti previsionali points-field).
function drawPointField(ctx, field, product) {
    const size = { x: DOMAIN.width, y: DOMAIN.height };
    // v1 filtra sui bounds mappa pad(0.35); qui la "vista" è il dominio fisso.
    const padded = padBounds({ s: DOMAIN.south, w: DOMAIN.west, n: DOMAIN.north, e: DOMAIN.east }, 0.35);
    const points = (field.points || [])
        .filter(point => boundsContains(padded, point.lat, point.lon))
        .map(point => {
            const pixel = domainPoint(point.lat, point.lon);
            return { ...point, x: pixel.x, y: pixel.y };
        });
    if (!points.length) return;
    const cell = Math.max(5, Math.min(10, Math.round(size.x / 150)));
    const radius = Math.max(76, Math.min(145, size.x / 8));
    const radiusSq = radius * radius;
    const scale = field.scale || product.scale;
    const palette = field.palette || product.palette;
    const paletteKeys = field.paletteKeys || product.paletteKeys;
    const paletteMode = field.paletteMode || product.paletteMode;
    const colorAt = makeColorSampler(scale, palette, paletteKeys, paletteMode);
    for (let y = 0; y < size.y; y += cell) {
        for (let x = 0; x < size.x; x += cell) {
            let weighted = 0;
            let weights = 0;
            let nearest = Infinity;
            for (const point of points) {
                const dx = x - point.x;
                const dy = y - point.y;
                const distanceSq = dx * dx + dy * dy;
                if (distanceSq > radiusSq) continue;
                nearest = Math.min(nearest, distanceSq);
                const weight = 1 / Math.pow(Math.max(18, distanceSq), 1.12);
                weighted += point.value * weight;
                weights += weight;
            }
            if (!weights) continue;
            const value = weighted / weights;
            const [r, g, b] = colorAt(value);
            const fade = Math.max(0, 1 - Math.sqrt(nearest) / radius);
            ctx.fillStyle = `rgba(${r},${g},${b},${0.18 + fade * 0.38})`;
            ctx.fillRect(x, y, cell + 1, cell + 1);
        }
    }
}

// Port di _applyDomainMask (maschera Sicilia condivisa via interpol.js).
function applyDomainMask(ctx, product) {
    if (product.fullDomain) return;
    if (product.domain === 'land') {
        compositeSicilyMask(ctx, 'destination-in');  // = keepOnlySicilyLandOnCanvas
        return;
    }
    compositeSicilyMask(ctx, 'destination-out');     // = punchSicilyLandFromCanvas
}

// Composito: componenti in ordine sea→land come createCompositeDataFieldLayer.
function orderedCompositeEntries(field) {
    const fields = Array.isArray(field?.fields) ? field.fields.slice() : [];
    return fields.sort((left, right) => {
        const order = { sea: 0, land: 1 };
        return (order[left.role] ?? 9) - (order[right.role] ?? 9);
    });
}

async function renderFieldToDomain(field, product) {
    const gen = (state.renderGen += 1);
    const canvas = state.canvas;
    if (!canvas) return;
    const parts = field.kind === 'composite'
        ? orderedCompositeEntries(field).map(entry => ({
            field: entry.field,
            product: { id: entry.productId, ...(entry.product || {}) },
        }))
        : [{ field, product }];

    // Ogni componente su canvas d'appoggio → maschera del SUO dominio → blit:
    // equivalente ai layer impilati v1 (le maschere composite non si mangiano
    // il componente già disegnato).
    const rendered = [];
    for (const part of parts) {
        const work = document.createElement('canvas');
        work.width = DOMAIN.width;
        work.height = DOMAIN.height;
        const ctx = work.getContext('2d', { alpha: true });
        if (part.field?.kind === 'points') drawPointField(ctx, part.field, part.product);
        else await drawGridFieldChunked(ctx, part.field, part.product, gen);
        if (gen !== state.renderGen) return;
        applyDomainMask(ctx, part.product);
        rendered.push(work);
    }

    const main = canvas.getContext('2d', { alpha: true });
    main.clearRect(0, 0, DOMAIN.width, DOMAIN.height);
    rendered.forEach(work => main.drawImage(work, 0, 0));
    refreshCanvasSource(state.map, SRC);
}

// ======== Sorgente/layer MapLibre ============================================
function ensureLayer() {
    const map = state.map;
    if (!state.canvas) {
        state.canvas = document.createElement('canvas');
        state.canvas.width = DOMAIN.width;
        state.canvas.height = DOMAIN.height;
    }
    if (!map.getSource(SRC)) {
        map.addSource(SRC, { type: 'canvas', canvas: state.canvas, coordinates: DOMAIN_COORDS, animate: false });
    }
    if (!map.getLayer(LYR)) {
        const before = map.getLayer(STATIONS_LYR) ? STATIONS_LYR : undefined;
        map.addLayer({
            id: LYR, type: 'raster', source: SRC,
            paint: { 'raster-opacity': state.opacity, 'raster-fade-duration': 0, 'raster-resampling': 'linear' },
        }, before);
    }
}

function removeLayer() {
    const map = state.map;
    try { if (map.getLayer(LYR)) map.removeLayer(LYR); } catch (e) {}
    try { if (map.getSource(SRC)) map.removeSource(SRC); } catch (e) {}
}

function clearCurrentField() {
    state.currentField = null;
    if (state.popup) state.popup.remove();
    const ctx = state.canvas?.getContext('2d', { alpha: true });
    if (!ctx) return;
    ctx.clearRect(0, 0, DOMAIN.width, DOMAIN.height);
    if (state.map?.getSource(SRC)) refreshCanvasSource(state.map, SRC);
}

async function updateOverlay() {
    if (!state.active || !state.catalog || !state.map) return;
    const seq = ++state.overlaySeq;
    ensureAvailableProduct();
    const step = currentStep();
    const product = currentProduct();
    if (!step || !product) {
        clearCurrentField();
        return;
    }
    try {
        const field = await fieldForStep(step, product, state.product);
        if (seq !== state.overlaySeq || !state.active) return;
        state.currentField = field;
        if (field?.ok) {
            ensureLayer();
            await renderFieldToDomain(field, { id: state.product, ...product });
        }
    } catch (error) {
        if (seq !== state.overlaySeq || !state.active) return;
        console.warn('[wsv2] WSLAM field non disponibile:', error);
        clearCurrentField();
    }
}

// ======== Valore puntuale + click (port valueAtLatLng) =======================
function latLngIsKnownLand(latlng) {
    const feature = getSicilyFeature();
    if (!latlng || !feature) return false;
    return featureContainsLatLng(feature, latlng.lat, latlng.lng);
}

function valueAtLatLngFromField(latlng, field, product) {
    if (!field || !product) return null;
    const isLand = latLngIsKnownLand(latlng);
    if (!product.fullDomain) {
        if (product.domain === 'land' && !isLand) return null;
        if (product.domain && product.domain !== 'land' && isLand) return null;
    }
    if (field.kind === 'points') {
        let nearest = null;
        let distance = Infinity;
        (field.points || []).forEach(point => {
            const dx = point.lat - latlng.lat;
            const dy = point.lon - latlng.lng;
            const d = dx * dx + dy * dy;
            if (d < distance) {
                nearest = point;
                distance = d;
            }
        });
        return nearest && distance < 0.16 ? { value: nearest.value, label: nearest.name || '' } : null;
    }
    const grid = field.grid;
    if (!grid?.values?.length || !grid.bounds) return null;
    const sample = sampleGridValue(grid, latlng.lat, latlng.lng, 'values', true);
    return sample ? { value: sample.value, label: '' } : null;
}

export function valueAtLatLng(latlng, field = state.currentField) {
    const product = currentProduct();
    if (!field || !product) return null;
    if (field.kind === 'composite') {
        const entries = Array.isArray(field.fields) ? field.fields : [];
        const orderedEntries = entries.slice().sort((left, right) => {
            const order = { land: 0, sea: 1 };
            return (order[left.role] ?? 9) - (order[right.role] ?? 9);
        });
        for (const entry of orderedEntries) {
            const hit = valueAtLatLngFromField(latlng, entry.field, entry.product);
            if (!hit) continue;
            const sourceLabel = entry.role === 'land' ? 'WSLAM LAM 2KM GRIB' : 'CMEMS mare';
            return { ...hit, productId: entry.productId, product: entry.product, sourceRole: entry.role, sourceLabel };
        }
        return null;
    }
    const hit = valueAtLatLngFromField(latlng, field, product);
    return hit ? { ...hit, productId: state.product, product } : null;
}

// Port di formattedValueParts.
function formattedValueParts(value, product) {
    const unit = product?.unit === 'C' ? '°C' : (product?.unit || '');
    const decimals = unit === '°C' ? 1 : (Math.abs(value) >= 10 ? 1 : 2);
    return {
        number: Number(value).toFixed(decimals).replace(/\.0$/, ''),
        unit,
    };
}

function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function clickHitsStations(e) {
    const map = state.map;
    if (!map.getLayer(STATIONS_LYR)) return false;
    try {
        return map.queryRenderedFeatures(e.point, { layers: [STATIONS_LYR] }).length > 0;
    } catch (err) {
        return false;
    }
}

function onMapClick(e) {
    if (!state.active) return;
    if (clickHitsStations(e)) return;
    const latlng = { lat: e.lngLat.lat, lng: e.lngLat.lng };
    const hit = valueAtLatLng(latlng);
    if (!hit) return;
    const displayProduct = hit.product || currentProduct();
    const value = formattedValueParts(hit.value, displayProduct);
    const source = hit.sourceLabel || displayProduct?.source || '';
    const html = `
<div class="wsv2-wslam-popup">
  <div class="wsv2-wslam-popup__title">${esc(displayProduct?.label || 'Campo dati')}</div>
  <div class="wsv2-wslam-popup__value">${esc(value.number)}${value.unit ? ' ' + esc(value.unit) : ''}</div>
  ${hit.label ? `<div class="wsv2-wslam-popup__place">${esc(hit.label)}</div>` : ''}
  ${source ? `<div class="wsv2-wslam-popup__source">${esc(source)}</div>` : ''}
</div>`.trim();

    if (!state.popup) {
        state.popup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, maxWidth: '260px', className: 'wsv2-wslam-popup-shell' });
    }
    state.popup.setLngLat(e.lngLat).setHTML(html).addTo(state.map);
}

// ======== API pubblica =======================================================
export function initWslam(map) {
    state.map = map;
    map.on('click', onMapClick);
}

export async function setWslamEnabled(on) {
    if (on === !!state.active) return state.active;
    state.timeRequestSeq += 1;
    if (on) {
        try {
            await Promise.all([loadCatalog(), loadSicilyFeature()]);
        } catch (error) {
            console.warn('[wsv2] WSLAM dati non disponibile:', error);
            return false;
        }
        state.active = true;
        if (window.wsv2) window.wsv2.wslamActive = true; // interpol cede il click (parità v1)
        ensureLayer();
        await updateOverlay();
        return true;
    }
    state.active = false;
    if (window.wsv2) window.wsv2.wslamActive = false;
    state.overlaySeq += 1;
    state.renderGen += 1;
    state.currentField = null;
    if (state.popup) state.popup.remove();
    removeLayer();
    return false;
}

export async function setWslamProduct(productId) {
    state.timeRequestSeq += 1;
    await loadCatalog();
    if (!state.catalog?.products?.[productId]) return false;
    const targetMs = stepTimeMs(currentStep()) || Date.now();
    state.overlaySeq += 1;
    state.product = productId;
    const matchingIndex = nearestStepIndexForTime(targetMs, productId);
    state.index = matchingIndex >= 0 ? matchingIndex : 0;
    if (state.active) await updateOverlay();
    return true;
}

export async function setWslamStep(i) {
    state.timeRequestSeq += 1;
    const steps = stepsForProduct();
    if (!steps.length) return false;
    const index = Math.max(0, Math.min(steps.length - 1, Number(i) || 0));
    if (index === state.index) return true;
    state.index = index;
    if (state.active) await updateOverlay();
    return true;
}

export function getWslamSteps(productId = state.product) {
    return stepsForProduct(productId).map((step, index) => ({
        index,
        key: step.key,
        label: step.label || step.key,
        time_ms: stepTimeMs(step),
    }));
}

export function getWslamStepIndex() {
    return state.index;
}

// Copertura temporale reale del prodotto caricato. I bordi sono ricavati da
// metà della cadenza osservata ai due estremi, mai da un orizzonte nominale.
export async function getWslamTemporalCoverage(productId = state.product) {
    await loadCatalog();
    const product = state.catalog?.products?.[productId];
    return {
        provider: 'wslam',
        productId,
        productLabel: product?.label || productId,
        ...getWslamTemporalRange(stepsForProduct(productId)),
    };
}

// Adapter per il tempo canonico. Le chiamate concorrenti vengono coalesciate
// dal catalogPromise e serializzate dal token: solo l'ultimo target può
// cambiare indice o completare un render.
export async function showWslamAtTime(target) {
    const requestSeq = ++state.timeRequestSeq;
    state.overlaySeq += 1;
    state.renderGen += 1;

    try {
        await loadCatalog();
    } catch (error) {
        if (requestSeq === state.timeRequestSeq) {
            console.warn('[wsv2] WSLAM copertura temporale non disponibile:', error);
        }
        return false;
    }
    if (requestSeq !== state.timeRequestSeq) return false;

    const selection = selectWslamStepAtTime(stepsForProduct(), target);
    if (!selection) {
        // Non lasciare un campo vecchio rappresentato come se appartenesse al
        // target fuori copertura. Il toggle resta attivo e indice invariato.
        if (state.active) clearCurrentField();
        return false;
    }

    state.index = selection.index;
    if (!state.active) return true;

    await updateOverlay();
    return requestSeq === state.timeRequestSeq
        && state.active
        && state.index === selection.index;
}
// Port di setWslamTime (aggancio timeline: scadenza più vicina all'istante).
export async function setWslamTime(target, options = {}) {
    state.timeRequestSeq += 1;
    await loadCatalog();
    const targetMs = target instanceof Date ? target.getTime() : Date.parse(String(target || ''));
    if (options.product && state.catalog?.products?.[options.product]) {
        state.product = options.product;
        state.index = Math.min(state.index, Math.max(0, stepsForProduct(options.product).length - 1));
    }
    const index = nearestStepIndexForTime(targetMs, state.product);
    if (index < 0) return false;
    state.index = index;
    if (state.active) await updateOverlay();
    return true;
}

// Slider opacità v1: clamp 0.2–1 (input percentuale → qui v ∈ [0,1]).
export function setWslamOpacity(v) {
    state.opacity = Math.min(1, Math.max(0.2, Number(v) || 0.82));
    const map = state.map;
    if (map?.getLayer(LYR)) map.setPaintProperty(LYR, 'raster-opacity', state.opacity);
}

// Catalogo prodotti per il pannello (id, label, short_label, disponibilità).
export async function getWslamProducts() {
    await loadCatalog();
    return Object.entries(state.catalog?.products || {}).map(([id, product]) => ({
        id,
        label: product.label || id,
        short_label: product.short_label || product.label || id,
        description: product.description || '',
        domain: product.domain || '',
        source: product.source || '',
        steps: stepsForProduct(id).length,
    }));
}

export function getWslamState() {
    return { active: state.active, product: state.product, index: state.index, opacity: state.opacity };
}
